import { createContext, useContext, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import { useIdleTimer } from "react-idle-timer";
import { removeToken, setToken } from "../api";
import auth from "../api/auth";
import { getServerError } from "./misc";
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [fetchingProfile, setFetchingProfile] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [activeMeterOwnership, setActiveMeterOwnership] = useState(null);
  useIdleTimer({
    timeout: 1000 * 60 * 20, //20 minutes
    disabled: !user,
    onIdle: () => {
      toast.loading(
        "Looks like you've been idle for too long. You're being automatically logged out for security reasons. Please log in again to continue",
        { duration: 5000 }
      );
      localStorage.clear();
      setTimeout(() => {
        window.location.reload();
      }, 5000);
    },
  });

  const meterOwnerships = user?.meter_ownerships || [];

  const setUserInfo = (data) => {
    setUser(data);
    setToken(data?.token);
    const new_meter_ownerships = data?.meter_ownerships || [];
    if (activeMeterOwnership) {
      const corrOwnership = new_meter_ownerships.find(
        (ownership) => ownership.id === activeMeterOwnership.id
      );
      if (corrOwnership) {
        setActiveMeterOwnership(corrOwnership);
      } else {
        setActiveMeterOwnership(data?.meter_ownerships[0]);
      }
    } else {
      setActiveMeterOwnership(data?.meter_ownerships[0]);
    }
  };

  const getProfile = async (token) => {
    setFetchingProfile(true);
    try {
      if (!token) {
        token = user?.token;
      }
      setToken(token);
      const res = await auth.getProfile();
      const data = { ...res?.data?.data, token };
      setUserInfo(data);
      localStorage.setItem("HH-AA", JSON.stringify(data));
      if (checking) {
        setChecking(false);
      }
      setFetchingProfile(false);
    } catch (e) {
      if (checking) {
        localStorage.clear();
        removeToken();
        setChecking(false);
      }
      setFetchingProfile(false);
    }
  };

  const updateProfile = (data) => {
    const currToken = user.token;
    setUserInfo({ ...data, token: currToken });
  };

  useEffect(() => {
    const item = localStorage.getItem("HH-AA");
    if (item) {
      // get profile here with token
      const parsedItem = JSON.parse(item);
      const token = parsedItem?.token;
      if (parsedItem && !token) {
        localStorage.clear();
        setChecking(false);
      } else {
        getProfile(token);
      }
    } else {
      localStorage.clear();
      setChecking(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (details) => {
    setLoading(true);
    try {
      const res = await auth.login(details);
      setUserInfo(res?.data?.data);
      localStorage.setItem("HH-AA", JSON.stringify(res?.data?.data));
      setLoading(false);
    } catch (e) {
      setLoading(false);
      toast.error(getServerError(e));
    }
  };

  const logout = async () => {
    localStorage.removeItem("HH-AA");
    setUser(null);
    removeToken();
  };

  if (checking) {
    return <div />;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        login,
        logout,
        loading,
        meterOwnerships,
        setActiveMeterOwnership,
        activeMeterOwnership,
        getProfile,
        fetchingProfile,
        hasMeters: meterOwnerships.length > 0,
        updateProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
